import React, { useRef, useEffect, useState } from 'react';
import PromptInput from '../ui/PromptInput';

const placeholderPrompts = [
    "Create a dashboard to analyze sales performance across different regions",
    "Build an interactive visualization of customer demographics",
    "Generate a report showing key metrics and trends over time",
    "Create a data exploration tool for marketing campaign analysis",
    "Build a visualization comparing product performance metrics",
    "Generate an interactive chart showing user engagement patterns",
    "Create a dashboard for tracking business KPIs",
    "Build a visualization of financial metrics and forecasts",
    "Generate a report analyzing customer behavior patterns",
    "Create an interactive tool for exploring market trends"
];

interface ProjectsPromptDialogProps {
    isOpen: boolean;
    onClose: () => void;
    onSubmit: (prompt: string) => void;
}

const ProjectsPromptDialog: React.FC<ProjectsPromptDialogProps> = ({
    isOpen,
    onClose,
    onSubmit
}) => {
    const dialogRef = useRef<HTMLDivElement>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [selectedModel, setSelectedModel] = useState("gpt-4o-mini");
    const [currentPlaceholder, setCurrentPlaceholder] = useState(placeholderPrompts[0]);
    const [ellipsis, setEllipsis] = useState('');
    const [isSliding, setIsSliding] = useState(false);

    const modelOptions = [
        { name: "GPT-o4 Mini", value: "o4-mini-2025-04-16", provider: "openai", providerTitle: "Open AI" },
        { name: "GPT-4o", value: "gpt-4o", provider: "openai", providerTitle: "Open AI" },
        { name: "Claude 3.7 Sonnet", value: "claude-3-7-sonnet-20250219", provider: "anthropic", providerTitle: "Anthropic" },
        { name: "Claude 3.5 Haiku", value: "claude-3-5-haiku-20241022", provider: "anthropic", providerTitle: "Anthropic" }
    ];

    // Handle ellipsis animation
    useEffect(() => {
        const ellipsisInterval = setInterval(() => {
            setEllipsis(prev => {
                if (prev.length >= 3) return '';
                return prev + '.';
            });
        }, 500);

        return () => clearInterval(ellipsisInterval);
    }, []);

    // Handle placeholder text animation
    useEffect(() => {
        let timeout: NodeJS.Timeout;
        
        const cyclePlaceholder = () => {
            setIsSliding(true);
            setCurrentPlaceholder(prev => {
                const currentIndex = placeholderPrompts.indexOf(prev);
                const nextIndex = (currentIndex + 1) % placeholderPrompts.length;
                return placeholderPrompts[nextIndex];
            });
            
            // Reset sliding state after animation
            timeout = setTimeout(() => {
                setIsSliding(false);
            }, 500);
        };

        const interval = setInterval(cyclePlaceholder, 3000);

        return () => {
            clearInterval(interval);
            clearTimeout(timeout);
        };
    }, []);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dialogRef.current && !dialogRef.current.contains(event.target as Node)) {
                onClose();
            }
        };

        if (isOpen) {
            document.addEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isOpen, onClose]);

    const handleSubmit = async (prompt: string) => {
        try {
            setIsLoading(true);
            await onSubmit(prompt);
        } catch (error) {
            console.error('Error handling prompt:', error);
        } finally {
            setIsLoading(false);
        }
    };

    const handleStopGeneration = () => {
        setIsLoading(false);
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 backdrop-blur-[2px] bg-white/30 flex items-center justify-center z-50">
            <div 
                ref={dialogRef}
                className="bg-white rounded-lg p-6 w-[600px] max-w-[90vw] shadow-xl border border-gray-200"
            >
                <h3 className="text-md font-medium mb-4">What would you like to build?</h3>
                <div className="[&_textarea]:text-sm [&_textarea]:min-h-[40px] [&_button]:p-1.5 [&_svg]:w-4 [&_svg]:h-4 [&_.text-\[10px\]]:text-xs [&_.h-6]:h-7">
                    <PromptInput
                        onSubmit={handleSubmit}
                        isLoading={isLoading}
                        onStopGeneration={handleStopGeneration}
                        selectedModel={selectedModel}
                        onModelSelect={setSelectedModel}
                        modelOptions={modelOptions}
                        showModelSelection={false}
                        showDataUpload={false}
                        minHeight="120px"
                        maxHeight="200px"
                        defaultRows={4}
                        placeholder={`${currentPlaceholder}${ellipsis}`}
                        className="transition-all duration-500"
                        textareaClassName={`leading-7 py-2 ${isSliding ? 'placeholder:translate-y-[-2px]' : 'placeholder:translate-y-0'} placeholder:transition-transform placeholder:duration-500`}
                        borderColor="border-gray-400"
                    />
                </div>
            </div>
        </div>
    );
};

export default ProjectsPromptDialog; 